"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export interface GuideTocItem {
  id: string;
  text: string;
}

/**
 * The "On this page" list beside a guide.
 *
 * Server-rendered as plain anchor links — the list and its hrefs are in the HTML, so a
 * crawler sees the page's own section index — and enhanced on the client to mark the
 * section currently in view. The observer watches the headings themselves rather than
 * scroll position, which stays correct however tall a section is.
 *
 * The same component renders inside a `<details>` on narrow screens and in the sticky
 * sidebar on wide ones; only the wrapper differs.
 */
export function GuideToc({
  items,
  label,
  className,
}: {
  items: readonly GuideTocItem[];
  label: string;
  className?: string;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const headings = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => element !== null);
    if (headings.length === 0) {
      return;
    }
    // A heading counts as current once it has scrolled into the top third of the
    // viewport; the topmost such heading wins so the marker moves in reading order.
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible.add(entry.target.id);
          } else {
            visible.delete(entry.target.id);
          }
        }
        const first = headings.find((heading) => visible.has(heading.id));
        if (first) {
          setActiveId(first.id);
        }
      },
      { rootMargin: "-96px 0px -66% 0px", threshold: 0 }
    );
    for (const heading of headings) {
      observer.observe(heading);
    }
    return () => {
      observer.disconnect();
    };
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  return (
    <nav aria-label={label} className={className}>
      <p className="mb-3 font-semibold text-foreground text-xs uppercase tracking-wide">
        {label}
      </p>
      <ol className="space-y-0.5 border-border/60 border-s">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                aria-current={active ? "location" : undefined}
                className={cn(
                  "-ms-px block border-s-2 py-1 ps-4 text-sm leading-snug transition-colors",
                  active
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                )}
                href={`#${item.id}`}
              >
                {item.text}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
